import type { CityConfig } from "../city-template/types";

export const kennebunkConfig: CityConfig = {
  city: "Kennebunk",
  state: "Maine",
  stateAbbr: "ME",
  slug: "kennebunk",
  route: "/cities/kennebunk",
  title: "Emergency & Fast Passport Renewal Kennebunk ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Kennebunk, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Kennebunk, Kennebunkport, Arundel, Wells, and southern Maine coast travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Kennebunk, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Kennebunk and southern Maine coast travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Kennebunk%20Maine%20beautiful%20coastal%20beach%20town%2C%20sandy%20beach%20with%20classic%20New%20England%20beach%20cottages%2C%20gentle%20ocean%20waves%2C%20summer%20afternoon%20light%2C%20charming%20coastal%20village%20atmosphere%2C%20professional%20photography%2C%20no%20people%20or%20visible%20text%2C%20soft%20blue%20sky%20with%20wispy%20clouds%20and%20green%20dune%20grass&width=1800&height=700&seq=kennebunk-me-hero-001&orientation=landscape",
  heroImageAlt: "Kennebunk Maine coastal beach with sandy shore and New England cottages",
  geoPosition: "43.3848;-70.5448",
  geoPlacename: "Kennebunk, Maine",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 90 miles (1.5 hours) from Kennebunk, ME",
  },
  nearbyCitiesZips: [
    { name: "Kennebunk", zips: "04043" },
    { name: "Kennebunkport", zips: "04046" },
    { name: "Arundel", zips: "04046" },
    { name: "Wells", zips: "04090" },
    { name: "Biddeford", zips: "04005" },
    { name: "Saco", zips: "04072" },
    { name: "Ogunquit", zips: "03907" },
    { name: "York", zips: "03909" },
    { name: "Sanford", zips: "04073" },
    { name: "Lyman", zips: "04002" },
    { name: "Alfred", zips: "04002" },
    { name: "Lebanon", zips: "04027" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Kennebunk departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Kennebunkport", route: "/kennebunkport" },
    { name: "Emergency passport help in Wells", route: "/cities/wells" },
    { name: "Urgent passport services in Ogunquit", route: "/cities/ogunquit" },
    { name: "Same-day passport help in Biddeford", route: "/cities/biddeford" },
    { name: "Expedited passport services in Saco", route: "/cities/saco" },
    { name: "Passport courier services in York", route: "/cities/york-me" },
    { name: "Rush passport help in Sanford", route: "/cities/sanford" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Kennebunk, Maine remotely via secure courier. We do not have a walk-in office in Kennebunk — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Kennebunk travelers.",
  fpcOfficeDistance: "Serving the southern Maine coast and surrounding beach towns",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Kennebunk and the southern Maine coast. From Kennebunk Beach to Goose Rocks Beach and the historic Kennebunkport area — we help Kennebunk travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Kennebunk neighborhoods and nearby towns including Kennebunkport, Arundel, Wells, Biddeford, Saco, Ogunquit, York, and Sanford. If you are in the southern Maine beach area, we have you covered.",
  howItWorksIntro: "Most Kennebunk travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "Kennebunk is a beautiful coastal town with easy beach access, but passport acceptance facilities still have strict requirements. Here are the most common mistakes we see from Kennebunk-area travelers.",
  testimonialsIntro: "Kennebunk travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Kennebunk? Here are the most common ones we hear from southern Maine coast travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Kennebunk international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Susan L.",
      location: "Kennebunk, ME",
      rating: 5,
      text: "We had a Caribbean cruise planned and my passport was expired. Fast Passport Center got my renewal done in 3 days. I shipped from the Kennebunk UPS Store and everything was perfect. Great for southern Maine beach towns.",
      service: "Passport Renewal",
    },
    {
      name: "Michael B.",
      location: "Kennebunkport, ME",
      rating: 5,
      text: "Business trip to London came up last minute and my passport was nowhere to be found. Fast Passport Center had my replacement in 2 days. Their courier service is a game-changer for coastal Maine.",
      service: "Lost Passport",
    },
    {
      name: "Patricia R.",
      location: "Wells, ME",
      rating: 5,
      text: "Needed emergency passports for our family of five after a last-minute opportunity to visit family in Ireland. Fast Passport Center handled everything, and we got all five passports in under a week. Amazing.",
      service: "Emergency Passport",
    },
    {
      name: "Thomas K.",
      location: "Arundel, ME",
      rating: 5,
      text: "Child passport for my daughter's first international trip. Fast Passport Center helped us navigate both parents' consent requirements and got the passport in 5 days. We were so grateful for the personal guidance.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "kennebunk-1",
      name: "Kennebunk Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 Dane St",
      city: "Kennebunk",
      state: "ME",
      zip: "04043",
      phone: "(207) 985-3311",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Kennebunk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Limited passport acceptance hours. Call ahead to confirm availability and book appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "kennebunk-2",
      name: "Kennebunkport Town Office",
      type: "Town Office Passport Acceptance",
      address: "21 Ocean Ave",
      city: "Kennebunkport",
      state: "ME",
      zip: "04046",
      phone: "(207) 967-4240",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles from Kennebunk",
      website: "https://www.kennebunkportme.gov/",
      websiteLabel: "Kennebunkport Town Office",
      notes: "Convenient for Kennebunkport and Arundel residents. Appointment preferred.",
      icon: "ri-government-line",
    },
    {
      id: "kennebunk-3",
      name: "Wells Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Spiller Dr",
      city: "Wells",
      state: "ME",
      zip: "04090",
      phone: "(207) 646-2551",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from Kennebunk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Wells and Ogunquit residents.",
      icon: "ri-mail-line",
    },
    {
      id: "kennebunk-4",
      name: "Biddeford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "35 Alfred St",
      city: "Biddeford",
      state: "ME",
      zip: "04005",
      phone: "(207) 282-4951",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from Kennebunk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility with more appointment slots. Popular for Biddeford and Saco residents.",
      icon: "ri-mail-line",
    },
    {
      id: "kennebunk-5",
      name: "Sanford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "13 School St",
      city: "Sanford",
      state: "ME",
      zip: "04073",
      phone: "(207) 324-5501",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Kennebunk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Rural option for Sanford and Lebanon residents.",
      icon: "ri-mail-line",
    },
    {
      id: "kennebunk-6",
      name: "York Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 Wedge Way",
      city: "York",
      state: "ME",
      zip: "03909",
      phone: "(207) 363-3411",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles from Kennebunk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Southernmost option for York and Ogunquit residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Kennebunk acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Kennebunk applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Kennebunk families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Kennebunk travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Kennebunk or Biddeford." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Kennebunk address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Kennebunk, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Kennebunk in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Kennebunk travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Kennebunk, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Kennebunk residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Kennebunk, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Kennebunk with no office visit required. We serve all Kennebunk neighborhoods including Kennebunk Beach and Goose Rocks Beach. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "How close is Kennebunk to the Boston Passport Agency?",
      a: "Kennebunk is approximately 90 miles from the Boston Passport Agency — about a 1.5-hour drive. With Fast Passport Center, you avoid that trip entirely. We courier your documents directly to State Department facilities, saving you time and stress.",
    },
    {
      q: "What acceptance facilities are near Kennebunk, Maine?",
      a: "Options near Kennebunk include the Kennebunk Post Office (04043), Kennebunkport Town Office (04046), Wells Post Office (04090), and Biddeford Post Office (04005). The York Post Office (03909) is also nearby for southern residents. We help you find the most convenient location.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "What if I live in Kennebunkport or Arundel?",
      a: "We serve all southern Maine coastal towns including Kennebunk, Kennebunkport, Arundel, Wells, Ogunquit, and York. Our overnight shipping labels work from any UPS location, and we can also arrange pickup service. Your beach town location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Kennebunk?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline and needs.",
    },
    {
      q: "What documents do I need for a first-time passport in Kennebunk?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Kennebunk?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Kennebunk families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "Is there a UPS Store near Kennebunk for shipping documents?",
      a: "Yes, there's a UPS Store in Kennebunk at 10 Dane St. We provide a prepaid overnight label — just drop your package at any UPS location or schedule a pickup. The Biddeford UPS Store on Elm Street is another nearby option.",
    },
    {
      q: "Can I get passport photos taken in Kennebunk?",
      a: "The CVS Pharmacy on Main Street in Kennebunk offers passport photo services. The Walgreens on US Route 1 in Wells and the Rite Aid in Biddeford also take passport photos. We recommend reviewing photos with us before your acceptance clerk visit.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Kennebunk", address: "10 Main St, Kennebunk, ME 04043", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Wells", address: "20 Spiller Dr, Wells, ME 04090", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Biddeford", address: "35 Alfred St, Biddeford, ME 04005", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Sanford", address: "13 School St, Sanford, ME 04073", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Saco", address: "1 Scammon St, Saco, ME 04072", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Kennebunk", address: "10 Dane St, Kennebunk, ME 04043", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart Photo Center — Biddeford", address: "50 Mariner Way, Biddeford, ME 04005", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walgreens — Ogunquit", address: "10 US Route 1, Ogunquit, ME 03907", hours: "Mon–Sun: 7AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Biddeford",
      address: "100 Mariner Way, Biddeford, ME 04005",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service BMV. Online appointment booking available. 12 miles from Kennebunk.",
      featured: true,
    },
    {
      name: "BMV — Kennebunk",
      address: "10 Dane St, Kennebunk, ME 04043",
      hours: "Mon–Fri: 9AM–4PM",
      note: "Limited services only. Full transactions require Biddeford location.",
      featured: false,
    },
    {
      name: "BMV — Sanford",
      address: "13 School St, Sanford, ME 04073",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Second nearest full-service BMV. 15 miles from Kennebunk.",
      featured: false,
    },
    {
      name: "BMV — York",
      address: "10 Wedge Way, York, ME 03909",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Southern option. About 18 miles from Kennebunk.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "30 miles from Kennebunk (35 min)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with direct flights to major U.S. hubs, Florida, and Canada. The primary international departure point for southern Maine coast travelers.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: true,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "140 miles from Kennebunk (2.5 hours)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport. Good alternative for northern Maine connections.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "90 miles from Kennebunk (1.5 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many southern Maine travelers connect through Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};